import React, { useLayoutEffect, useRef, useState } from "react";

export interface DistributionCurveProps {
  /** How many players put this option in slot 1..5. Always length 5. */
  counts: number[];
  /** Line + fill colour — the option's own colour, so the curve belongs to its bar. */
  color: string;
  /** Slot (1-5) the viewer put this option in, marked like their avatar is elsewhere. */
  viewerSlot?: number;
  /** Panel background, used for the ring that keeps dots legible over the curve. */
  surface: string;
  height: number;
  scale: number;
}

/**
 * The cue colours are tuned to sit behind dark text on a card, so several of them (yellow above
 * all) vanish as a 2px line on the panel. Mixing toward near-black keeps the hue recognisable —
 * the row's own colour — while giving the line and its dots something to read against.
 */
function inkOf(hex: string, mix = 0.38): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) =>
    Math.round(c * (1 - mix) + 0x2a * mix)
  );
  return `#${ch.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Fritsch-Carlson monotone cubic interpolation.
 *
 * A plain Catmull-Rom spline through five counts overshoots: between a 0 and a 6 it dips below
 * the baseline, drawing a negative number of players. Monotone tangents cannot overshoot, so the
 * curve is smooth without inventing peaks, valleys, or impossible values.
 */
function monotonePath(pts: { x: number; y: number }[]): string {
  const n = pts.length;
  if (n < 2) return "";

  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(pts[i + 1].x - pts[i].x);
    slope.push((pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x));
  }

  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += ` C ${pts[i].x + h} ${pts[i].y + m[i] * h} ${pts[i + 1].x - h} ${
      pts[i + 1].y - m[i + 1] * h
    } ${pts[i + 1].x} ${pts[i + 1].y}`;
  }
  return d;
}

/**
 * Where a lobby is too big to name everyone, the shape of the vote is the story: did the group
 * agree this option belongs at the front, or did it split? One filled curve per option row,
 * plotted over the same five slots the avatars used.
 *
 * No y-axis — the peak carries its own count, and the question is the shape, not the exact number
 * of players three slots along.
 */
export default function DistributionCurve({
  counts,
  color,
  viewerSlot,
  surface,
  height,
  scale,
}: DistributionCurveProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const labelBand = Math.round(13 * scale); // headroom reserved for the peak count
  const dotR = Math.round(4 * scale);
  const selfR = dotR + Math.round(3 * scale);
  const plotTop = labelBand + selfR;
  const baseline = height - Math.round(3 * scale);
  const max = Math.max(...counts, 1);
  const ink = inkOf(color);

  // Slot 1 sits over the "1" marker and slot 5 over the "5", so the curve spans the whole band the
  // avatar columns used to fill. Inset by the widest dot so an end marker never crowds the digits.
  const inset = selfR + Math.round(2 * scale);
  const xOf = (i: number) => inset + (i / (counts.length - 1)) * Math.max(width - 2 * inset, 0);
  const yOf = (c: number) => baseline - (c / max) * (baseline - plotTop);
  const pts = counts.map((c, i) => ({ x: xOf(i), y: yOf(c) }));

  const line = width > 0 ? monotonePath(pts) : "";
  const area = line ? `${line} L ${pts[pts.length - 1].x} ${baseline} L ${pts[0].x} ${baseline} Z` : "";

  // Only the extreme is labelled — a number on every point is noise, and the shape is the point.
  const peak = counts.indexOf(max);
  const labelX = Math.min(Math.max(xOf(peak), 14 * scale), Math.max(width - 14 * scale, 0));

  return (
    <div className="vb-curve" ref={wrapRef} style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} style={{ display: "block", overflow: "visible" }}>
          <path d={area} fill={color} fillOpacity={0.32} />
          <line
            className="vb-curve-base"
            x1={0}
            y1={baseline}
            x2={width}
            y2={baseline}
            strokeWidth={1}
          />
          <path
            d={line}
            fill="none"
            stroke={ink}
            strokeWidth={2 * scale}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* The data only exists at five discrete slots — the dots say so, the curve just reads it. */}
          {pts.map((p, i) => {
            const isSelf = viewerSlot === i + 1;
            return (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={dotR}
                  fill={ink}
                  stroke={surface}
                  strokeWidth={2 * scale}
                />
                {isSelf && (
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={selfR}
                    fill="none"
                    stroke="var(--main-color, #d36667)"
                    strokeWidth={2 * scale}
                  />
                )}
              </g>
            );
          })}
          <text
            className="vb-curve-peak"
            x={labelX}
            y={labelBand - Math.round(3 * scale)}
            textAnchor="middle"
            style={{ fontSize: `${Math.round(11 * scale)}px` }}
          >
            {max}
          </text>
        </svg>
      )}
    </div>
  );
}
